import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const DailyLottery = () => {
  const { token } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, pageSize: 30, order: '' });
  const [tempFilters, setTempFilters] = useState({ page: 1, pageSize: 30, order: '' });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);
  const { can } = usePermissions();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get('/api/admin/lottery/daily-lottery', {
        headers: { token },
        params
      });

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching daily lottery:', error);
      toast.error('Failed to fetch daily lottery data');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Client-side sorting effect
  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? '';
      let bValue = b[sort.key] ?? '';
      if (sort.key === 'totalParticipants' || sort.key === 'totalAmount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'resultAnnounced') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === 'createdAt' || sort.key === 'resultDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  const clearFilters = () => {
    const defaultFilters = { page: 1, pageSize: 30, order: '' };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const getStatusBadge = (resultAnnounced) =>
    resultAnnounced ? (
      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
        Result Announced
      </span>
    ) : (
      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
        Pending
      </span>
    );

  const thCls = "text-left";
  const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Daily <span className="text-gradient-ginox">Lottery</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View and manage daily lottery events.
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Order
            </label>
            <select
              value={tempFilters.order}
              onChange={(e) => handleTempFilterChange('order', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={tempFilters.pageSize}
              onChange={(e) => handleTempFilterChange('pageSize', parseInt(e.target.value))}
              className={inputCls}
            >
              {[10, 15, 25, 50, 100].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              value={tempFilters.page}
              onChange={(e) => handleTempFilterChange('page', parseInt(e.target.value))}
              className={inputCls}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              <tr>
                <td className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0" style={{ minWidth: '1200px' }}>
              <thead>
                <tr>
                  <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className={thCls}>S.No</SortableTableHeader>
                  <SortableTableHeader sortKey="lotteryId" currentSort={sort} onSort={handleSort} className={thCls}>Lottery ID</SortableTableHeader>
                  <SortableTableHeader sortKey="startTime" currentSort={sort} onSort={handleSort} className={thCls}>Start Time</SortableTableHeader>
                  <SortableTableHeader sortKey="endTime" currentSort={sort} onSort={handleSort} className={thCls}>End Time</SortableTableHeader>
                  <SortableTableHeader sortKey="resultTime" currentSort={sort} onSort={handleSort} className={thCls}>Result Time</SortableTableHeader>
                  <SortableTableHeader sortKey="ticketCount" currentSort={sort} onSort={handleSort} className={thCls}>Ticket Count</SortableTableHeader>
                  <SortableTableHeader sortKey="resultAnnounced" currentSort={sort} onSort={handleSort} className={thCls}>Status</SortableTableHeader>
                  <SortableTableHeader sortKey="winners" currentSort={sort} onSort={handleSort} className={thCls}>Winners</SortableTableHeader>
                  <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className={thCls}>Created At</SortableTableHeader>
                  <th className="border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => (
                  <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className={`${tdCls} font-mono`}>
                      {(filters.page - 1) * filters.pageSize + index + 1}
                    </td>
                    <td className={`${tdCls} font-mono`}>{item.lotteryId}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.startTime)}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.endTime)}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.resultTime)}</td>
                    <td className={`${tdCls} font-mono`}>{item.ticketCount}</td>
                    <td className={tdCls}>{getStatusBadge(item.resultAnnounced)}</td>
                    <td className={tdCls}>
                      {item.winners && item.winners.length > 0 ? (
                        <span className="text-emerald-400">{item.winners.length} winner(s)</span>
                      ) : (
                        <span className="text-muted-foreground">No winners</span>
                      )}
                    </td>
                    <td className={`${tdCls} font-mono text-xs`}>{formatDate(item.createdAt)}</td>
                    <td className={tdCls}>
                      {can('GET:/api/admin/lottery/daily-lottery/:lottery_id/participants') && (
                        <Link
                          to={`/lottery/daily/${item.lotteryId}/participants`}
                          className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
                        >
                          View Participants
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default DailyLottery;
